// English catalogue (source of AppTranslations).

export const en = {
  characteristics: {
    common: {
      na: 'N/A',
      no: 'No',
      source: 'Source',
      sourceCalculated: 'Calculated',
      years: 'years',
      yes: 'Yes',
    },
    developmentPotential: {
      tooltips: {
        far: {
          calculation: 'Total gross floor area divided by land area. Example: 1000m² building on 500m² land = FAR of 2.0',
          categories: [
            {
              description: 'Rural, large lot single-family',
              label: 'Low density',
              range: 'FAR 0.1-0.5',
            },
            {
              description: 'Suburban, townhouses',
              label: 'Medium density',
              range: 'FAR 0.5-1.5',
            },
            {
              description: 'Urban, mid-rise apartments',
              label: 'High density',
              range: 'FAR 1.5-3.0',
            },
            {
              description: 'Downtown, high-rise buildings',
              label: 'Very high density',
              range: 'FAR >3.0',
            },
          ],
          importance: 'FAR is the primary metric for understanding development intensity and land use efficiency. Higher FAR indicates more intensive land use and typically higher property values per square meter of land.',
          limitations: 'FAR regulations vary by zone. Check local urban planning documents for maximum allowed FAR in your zone.',
          shortDescription: 'Ratio of total building floor area to land parcel area',
          source: 'Calculated from Swiss Building Registry (GWR) and Land Registry',
          title: 'Floor Area Ratio (FAR)',
        },
        floorPotential: {
          calculation: 'Difference between maximum permitted number of floors (from local urban planning regulations) and current floor count',
          importance: 'Additional floors represent the most direct path to increasing building capacity and property value. Each additional floor can add significant usable area and rental/sales revenue potential.',
          limitations: 'Floor additions require structural analysis, foundation capacity assessment, and may trigger requirements for elevators, fire safety upgrades, and parking additions. Costs can be substantial.',
          shortDescription: 'Comparison of current floors vs maximum allowed by zoning',
          source: 'Local Urban Planning (LUP) regulations and Swiss Building Registry (GWR)',
          title: 'Floor Count Potential',
        },
        heightPotential: {
          calculation: 'Difference between maximum permitted height (from local urban planning regulations) and current building height',
          importance: 'Understanding height potential is crucial for assessing vertical expansion possibilities, property development value, and compliance with local regulations. Additional height can significantly increase usable floor area and property value.',
          limitations: 'Maximum height restrictions may be subject to other factors like setbacks, shadow studies, heritage constraints, or neighborhood opposition. Professional planning consultation recommended.',
          shortDescription: 'Comparison of current height vs maximum allowed by local zoning',
          source: 'Local Urban Planning (LUP) regulations and Swiss Building Registry (GWR)',
          title: 'Building Height Potential',
        },
      },
    },
    header: {
      subtitle: 'Detailed information about this building',
      title: 'Building Characteristics',
    },
    neighborhood: {
      ageRanges: {
        '1919_1945': '1919-1945',
        '1946_1960': '1946-1960',
        '1961_1970': '1961-1970',
        '1971_1980': '1971-1980',
        '1981_1990': '1981-1990',
        '1991_2000': '1991-2000',
        '2001_2010': '2001-2010',
        '2011_2020': '2011-2020',
        post2020: 'After 2020',
        pre1919: 'Before 1919',
      },
      badges: {
        buildingOutlier: 'Building Outlier',
      },
      cards: {
        ageDistribution: {
          title: 'Building Age Distribution',
        },
        analysis: {
          tooltip: {
            source: 'GWR, Popety.io',
          },
        },
        areaComparison: {
          title: 'Footprint Area Comparison',
          tooltip: {
            calculation: "Compares this building's ground floor area with the average and maximum areas of neighboring buildings",
            description: "How this building's footprint compares to surrounding buildings",
            importance: 'Building footprint relative to neighbors indicates land use intensity, development density, and whether the building is typical for the area.',
            limitations: "Footprint alone doesn't reflect total building size. Check floor count and gross floor area for complete size assessment.",
            title: 'Area Comparison with Neighbors',
          },
        },
        cohesionBreakdown: {
          title: 'Cohesion Breakdown',
          tooltip: {
            calculation: 'Weighted average of temporal (40%), physical (30%), typological (20%), and density (10%) cohesion scores',
            description: 'Shows how homogeneous the neighborhood is across multiple dimensions',
            importance: 'A high cohesion score indicates a well-defined neighborhood with consistent building characteristics',
            title: 'Cohesion Breakdown',
          },
        },
        comparativeAnalysis: {
          title: 'Comparative Analysis',
        },
        conformityBreakdown: {
          title: 'Building Conformity Breakdown',
          tooltip: {
            calculation: "Compares this building's characteristics against the typical quartier profile",
            description: 'Shows how well this specific building matches the quartier pattern',
            importance: 'Low conformity in a cohesive quartier indicates an outlier building, which may affect valuation, development restrictions, or architectural harmony.',
            title: 'Building Conformity Breakdown',
          },
        },
        heightComparison: {
          title: 'Height Comparison',
          tooltip: {
            calculation: "Compares this building's total height with the average and maximum heights of neighboring buildings within a defined radius (typically 100-200m)",
            description: "How this building's height compares to surrounding buildings",
            importance: 'Building height relative to neighbors affects views, sunlight exposure, privacy, architectural harmony, and compliance with local zoning regulations.',
            limitations: 'Comparison based on buildings within the search radius. Local topography and terrain elevation differences are not reflected in this metric.',
            title: 'Height Comparison with Neighbors',
          },
        },
        neighborhoodAnalysis: {
          title: 'Neighborhood & Building Analysis',
          tooltip: {
            calculation: 'Neighborhood Cohesion measures how similar surrounding buildings are. Building Conformity measures how well THIS building matches the neighborhood pattern.',
            description: 'Analyzes both neighborhood cohesion and how well this building fits into the detected quartier',
            importance: 'High cohesion with low conformity indicates an outlier building in a cohesive neighborhood, which may affect valuation and development potential.',
            title: 'Neighborhood & Building Analysis',
          },
        },
        quartierProfile: {
          title: 'Neighborhood Profile',
        },
        volumeComparison: {
          title: 'Volume Comparison',
          tooltip: {
            calculation: "Compares this building's total enclosed volume with the average and maximum volumes of neighboring buildings",
            description: "How this building's total volume compares to surrounding buildings",
            importance: 'Building volume is a key indicator of total building mass and development intensity. Used for energy modeling and HVAC capacity planning.',
            limitations: 'Volume calculations may use simplified geometric assumptions. Actual internal volume may vary due to roof geometry and interior layouts.',
            title: 'Volume Comparison with Neighbors',
          },
        },
      },
      chart: {
        areaAxis: 'Area (m²)',
        buildingsAxis: 'Buildings',
        heightAxis: 'Height (m)',
        neighborhoodAvg: 'Neighborhood Avg.',
        neighborhoodMax: 'Neighborhood Max.',
        thisBuilding: 'This Building',
        volumeAxis: 'Volume (m³)',
      },
      charts: {
        area: {
          source: 'Popety.io, GWR',
        },
        height: {
          source: 'Popety.io, GWR',
        },
        volume: {
          source: 'Popety.io, GWR',
        },
      },
      cohesion: {
        density: {
          label: 'Density (10%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Based on the number of buildings per square kilometer. Higher density suggests urban quartier.',
            description: 'Measures development density',
            title: 'Density Cohesion',
          },
        },
        physical: {
          label: 'Physical Cohesion (30%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Based on similarity in height, area, and floor count. Higher scores indicate uniform building characteristics.',
            description: 'Measures architectural similarity',
            title: 'Physical Cohesion',
          },
        },
        temporal: {
          label: 'Temporal Cohesion (40%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Based on similarity of construction years. Higher scores indicate buildings from the same era.',
            description: 'Measures if buildings were constructed in the same period',
            title: 'Temporal Cohesion',
          },
        },
        typological: {
          label: 'Typological Cohesion (20%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Based on the prevalence of similar building categories (e.g., single-family, multi-family, etc.)',
            description: 'Measures building type similarity',
            title: 'Typological Cohesion',
          },
        },
      },
      comparison: {
        aboveAverage: 'Above Average',
        belowAverage: 'Below Average',
        similar: 'Similar',
      },
      conformity: {
        fitsWell: 'Fits Well',
        outlier: 'Outlier',
        partialFit: 'Partial fit',
        physicalMatch: {
          label: 'Physical Match (40%)',
          source: 'Popety.io, swissSURFACE3D',
          tooltip: {
            calculation: 'Average of height, area, and floor count similarity. ±20-30% = high match',
            description: "How well this building's size and height match the typical quartier",
            title: 'Physical Match',
          },
        },
        temporalMatch: {
          label: 'Temporal Match (40%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Compares building year against typical year. ±5 years = 100%, ±10 years = 75%, ±20 years = 50%',
            description: "How well this building's construction year matches the typical quartier period",
            title: 'Temporal Match',
          },
        },
        tooltip: {
          source: 'Popety.io',
        },
        typologicalMatch: {
          label: 'Typological Match (20%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: '100 if same category as typical, 0 if different',
            description: "Whether this building's type matches the dominant quartier category",
            title: 'Typological Match',
          },
        },
      },
      errors: {
        loadFailed: 'Failed to load neighborhood data',
      },
      loading: 'Loading neighborhood data...',
      metrics: {
        areaVsAverage: {
          label: 'Area vs Average',
          tooltip: {
            calculation: 'Percentage difference from mean area of buildings within search radius',
            description: "How this building's footprint compares to nearby buildings",
            importance: 'Indicates if building is typical or unusually large/small for the area. Affects FAR and development potential.',
            source: 'Popety.io, GWR',
            title: 'Area vs Neighborhood Average',
          },
        },
        avgNeighborAge: {
          label: 'Avg Neighbor Age',
          tooltip: {
            calculation: 'Average of ages of all buildings within search radius',
            description: 'Mean age of surrounding buildings',
            importance: 'Indicates if the neighborhood is predominantly old (>50 years), established (20-50 years), or new (<20 years).',
            source: 'Popety.io, GWR',
            title: 'Average Neighbor Age',
          },
          value: '{{years}} years',
        },
        buildingConformity: 'Building Conformity',
        buildingDensity: {
          label: 'Building Density',
          tooltip: {
            calculation: 'Count of buildings within a defined radius divided by the area of that radius',
            description: 'Number of buildings per unit area in the surrounding area',
            importance: 'Indicates whether the area is urban, suburban, or rural. High density suggests urban environment with closer buildings.',
            source: 'Popety.io, GWR',
            title: 'Building Density',
          },
          value: '{{density}} buildings/km²',
        },
        buildingsAnalyzed: 'Buildings Analyzed',
        confidence: 'Confidence',
        heightVsAverage: {
          label: 'Height vs Average',
          tooltip: {
            calculation: 'Percentage difference from mean height of buildings within search radius',
            description: "How this building's height compares to nearby buildings",
            importance: 'Buildings significantly taller or shorter than neighbors may face zoning restrictions or stand out visually.',
            source: 'Popety.io, GWR',
            title: 'Height vs Neighborhood Average',
          },
        },
        neighborhoodCohesion: 'Neighborhood Cohesion',
        neighboringBuildings: {
          label: 'Neighboring Buildings',
          tooltip: {
            calculation: 'Number of buildings found within the selected search radius',
            description: 'Count of buildings within the analysis radius',
            importance: 'More neighbors provide better statistical comparison. Fewer than 10 neighbors may indicate rural area or limited data reliability.',
            source: 'Popety.io, GWR',
            title: 'Neighboring Buildings',
          },
        },
        volumeVsAverage: {
          label: 'Volume vs Average',
          tooltip: {
            calculation: 'Percentage difference from mean volume of buildings within search radius',
            description: "How this building's volume compares to nearby buildings",
            importance: 'Larger volumes indicate more substantial structures. Affects heating costs and development density.',
            source: 'Popety.io, GWR',
            title: 'Volume vs Neighborhood Average',
          },
        },
      },
      neighboringBuildings: 'Neighboring Buildings',
      noData: 'No neighborhood data available',
      notes: {
        differsByPattern: 'Differs from neighborhood pattern',
        physicalMismatch: 'Physical characteristics differ from neighborhood average',
        temporalMismatch: 'Building age differs from neighborhood pattern',
        title: 'Notes',
        typologicalMismatch: 'Building type differs from neighborhood standard',
      },
      profile: {
        description: {
          label: 'Description',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Generated from typical building year and dominant category',
            description: 'Summary characterization of the neighborhood',
            importance: "Provides quick overview of neighborhood character (e.g., '1960s residential' or '1980s mixed-use').",
            title: 'Quartier Description',
          },
        },
        developmentCategories: {
          source: 'Popety.io, GWR',
        },
        developmentPattern: {
          categories: {
            mixed: {
              description: 'Moderate cohesion, varied development (score 55-74)',
              label: 'Mixed',
            },
            organic: {
              description: 'Low cohesion, diverse development (score <55)',
              label: 'Organic',
            },
            planned: {
              description: 'High cohesion, uniform development (score ≥75)',
              label: 'Planned',
            },
          },
          label: 'Development Pattern',
          source: 'Popety.io, GWR',
          tooltip: {
            description: 'Classification of neighborhood development style',
            importance: 'Planned areas have stricter zoning. Organic areas offer more renovation flexibility.',
            title: 'Development Pattern',
          },
        },
        typicalArea: {
          label: 'Typical Area',
          source: 'Popety.io',
          tooltip: {
            calculation: 'Mean footprint area of buildings in the neighborhood',
            description: 'Representative building footprint for the quartier',
            importance: 'Indicates typical lot coverage and building scale in the area.',
            title: 'Typical Area',
          },
        },
        typicalCategory: {
          label: 'Typical Category',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Most common building category among neighborhood buildings',
            description: 'Dominant building type in the quartier',
            importance: 'Defines neighborhood character: residential, commercial, industrial, or mixed-use.',
            title: 'Typical Category',
          },
        },
        typicalHeight: {
          label: 'Typical Height',
          source: 'Popety.io, swissSURFACE3D',
          tooltip: {
            calculation: 'Mean height of buildings in the neighborhood',
            description: 'Representative building height for the quartier',
            importance: 'Indicates if area is predominantly low-rise (<10m), mid-rise (10-25m), or high-rise (>25m).',
            title: 'Typical Height',
          },
        },
        typicalYear: {
          label: 'Typical Construction Year',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Mean construction year of buildings in the neighborhood',
            description: 'Most representative construction year for the quartier',
            importance: 'Defines the architectural era and likely building standards for the area.',
            title: 'Typical Building Year',
          },
        },
      },
      radius: {
        label: 'Analysis Radius',
        value: '{{value}}m',
      },
      statistics: {
        area: {
          mean: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: 'Arithmetic mean of all building areas',
              description: 'Average building footprint in the neighborhood',
              importance: 'Indicates typical lot size. Influenced by outliers.',
              title: 'Mean Area',
            },
          },
          median: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: '50th percentile of building areas',
              description: 'Middle value of neighborhood building areas',
              importance: 'More representative than mean when outliers exist.',
              title: 'Median Area',
            },
          },
          range: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: 'Smallest and largest building footprints in neighborhood',
              description: 'Minimum and maximum building areas',
              importance: 'Shows size diversity. Wide range indicates mixed building scales.',
              title: 'Area Range',
            },
          },
          title: 'Area',
        },
        floor: {
          floorsRange: '{{min}} - {{max}} floors',
          floorsValue: '{{floors}} floors',
          mean: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: 'Arithmetic mean of all building floor counts',
              description: 'Average number of floors in the neighborhood',
              importance: 'Indicates typical building scale. Influenced by outliers.',
              title: 'Mean Floor Count',
            },
          },
          median: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: '50th percentile of floor counts',
              description: 'Middle value of neighborhood floor counts',
              importance: 'More representative than mean when outliers exist.',
              title: 'Median Floor Count',
            },
          },
          range: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: 'Lowest and highest floor counts in neighborhood',
              description: 'Minimum and maximum floor counts',
              importance: 'Shows vertical diversity. Wide range indicates mixed rise levels.',
              title: 'Floor Count Range',
            },
          },
          title: 'Floors',
        },
        height: {
          mean: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: 'Arithmetic mean of all building heights',
              description: 'Average building height in the neighborhood',
              importance: 'Indicates typical building scale. Influenced by outliers.',
              title: 'Mean Height',
            },
          },
          median: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: '50th percentile of building heights',
              description: 'Middle value of neighborhood building heights',
              importance: 'More representative than mean when outliers exist.',
              title: 'Median Height',
            },
          },
          range: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: 'Lowest and highest building heights in neighborhood',
              description: 'Minimum and maximum building heights',
              importance: 'Shows height diversity. Wide range indicates mixed development.',
              title: 'Height Range',
            },
          },
          title: 'Height',
        },
        mean: 'Mean',
        median: 'Median',
        range: 'Range',
      },
      title: 'Neighborhood Context',
      tooltips: {
        source: 'Data source',
        sourceCalculated: 'Calculated from Swiss Building Registry data',
      },
      warnings: {
        outlierDescription: 'This building does not fit the cohesive neighborhood pattern. It may be an outlier in terms of age, size, or type compared to surrounding buildings.',
        outlierTitle: 'This building is an outlier',
      },
    },
    overview: {
      ageCategories: {
        contemporary: {
          description: 'Built with relatively recent construction methods and materials',
          label: 'Contemporary',
          range: '10-30 years',
        },
        historic: {
          description: 'Historic building potentially with heritage value and character features',
          label: 'Historic',
          range: '>50 years',
        },
        mature: {
          description: 'Established building that may benefit from renovation or modernization',
          label: 'Mature',
          range: '30-50 years',
        },
        new: {
          description: 'Recently constructed with modern building standards and energy efficiency',
          label: 'New',
          range: '<10 years',
        },
      },
      cards: {
        dimensions: {
          buildingVolume: 'Building Volume',
          footprintArea: 'Footprint Area',
          grossFloorArea: 'Gross Floor Area',
          groundElevation: 'Ground Elevation',
          title: 'Physical Dimensions',
          tooltips: {
            elevation: {
              calculation: 'Measured from LiDAR data',
              description: 'Height of the ground level at the building location above sea level',
              importance: 'Important for flood risk assessment, drainage planning, and understanding local topography',
              source: 'Popety.io, swissSURFACE3D',
              title: 'Ground Elevation',
            },
            footprintImportance: 'Determines land coverage and is crucial for calculating development potential and compliance with zoning regulations',
            grossFloorArea: {
              calculation: 'Sum of all floor areas including walls, stairways, and common areas. Calculated from building registry or footprint area × floor count',
              description: 'Total floor area of all floors, measured to the outer face of external walls',
              importance: 'Key metric for assessing development potential, notably the IBUS calculation',
              limitations: 'May include non-habitable areas like corridors and technical rooms. Net usable area is typically 10-25% smaller',
              source: 'GWR, Popety.io',
              title: 'Gross Floor Area',
            },
            heightCalculation: 'Height measured from ground level to roof ridge using LiDAR data and official building surveys',
            heightImportance: 'Critical for zoning compliance, neighbor relations, and determining building classification',
            volume: {
              calculation: 'Measured from LiDAR data',
              description: 'Total enclosed volume of the building from foundation to roof',
              importance: 'Used for energy modeling, HVAC sizing, and development density calculations, notably for the IM (Mass Index)',
              source: 'Popety.io, swissSURFACE3D',
              title: 'Building Volume',
            },
          },
        },
        occupancy: {
          avgFloorArea: 'Avg. Floor Area',
          avgFloorHeight: 'Avg. Floor Height',
          buildingCategory: 'Building Category',
          numberOfDwellings: 'Number of Dwellings',
          popetyClassification: 'Popety.io Classification',
          primaryUse: 'Primary Use',
          title: 'Occupancy & Type',
          tooltips: {
            avgFloorArea: {
              calculation: 'Calculated by dividing gross floor area by the number of floors',
              description: 'The average area of each floor in the building',
              importance: 'Shows how much usable floor area a typical storey provides',
              source: 'Popety.io',
              title: 'Average Floor Area',
            },
            avgFloorHeight: {
              calculation: 'Calculated by dividing total building height by the number of floors',
              description: 'The average vertical distance between floors in the building',
              importance: "Indicates typical ceiling height and the building's vertical comfort",
              source: 'Popety.io',
              title: 'Average Floor Height',
            },
            category: {
              calculation: 'Assigned by the federal GWR based on their dominant use',
              description: 'Subdivision of buildings according to their intended use',
              importance: 'Groups the building by its official use so it can be compared with similar buildings',
              source: 'GWR',
              title: 'Building Category',
            },
            dwellings: {
              calculation: 'Counted from official building registries',
              description: 'Total count of individual residential units in the building',
              importance: 'Counts the homes in the building, which drives occupancy and valuation',
              source: 'GWR',
              title: 'Number of Dwellings',
            },
            popetyClassification: {
              calculation: 'Computed by Popety.io based on building data, usage patterns, and market analysis',
              description: "Building classification determined by Popety.io's proprietary algorithm",
              importance: 'Provides a standardized classification for comparison and valuation purposes',
              title: 'Popety.io Classification',
            },
            primaryUse: {
              calculation: 'The current EUROSTAT* classification subdivides all construction works into 2 sections (building / civil engineering), 6 divisions (2/4), 20 groups (10/10) and 46 classes (21/25).',
              description: 'Breakdown of buildings according to the EUROSTAT+ classification',
              importance: 'Identifies the dominant use, which drives zoning, valuation and comparables',
              source: 'GWR',
              title: 'Building Class',
            },
          },
        },
        occupancyType: {
          title: 'Occupancy & Type',
        },
        physicalDimensions: {
          title: 'Physical Dimensions',
        },
        status: {
          constructionYear: 'Construction Year',
          currentStatus: 'Current Status',
          lastRenovation: 'Last Renovation',
          minergieCertified: 'Minergie Certified',
          protectedStatus: 'Protected Status',
          title: 'Status & Condition',
          tooltips: {
            constructionYear: {
              calculation: 'Taken from the federal building register (GWR) construction year',
              description: 'The year when the building was originally completed',
              importance: 'Dates the building and anchors age, condition and renovation analysis',
              limitations: 'For older buildings, may be estimated from construction period range. Major reconstructions may not update this date',
              source: 'GWR',
              title: 'Construction Year',
            },
            minergie: {
              calculation: 'Awarded by Minergie association based on meeting strict energy efficiency standards',
              description: 'Swiss energy efficiency certification for buildings',
              importance: 'Indicates superior energy efficiency, higher comfort, and typically lower operating costs',
              source: 'Federal Office of Energy SFOE',
              title: 'Minergie Certification',
            },
            protected: {
              calculation: 'Assessed by cantonal heritage authorities based on historical, architectural, or cultural significance',
              description: 'Heritage protection level assigned to the building by cantonal authorities',
              importance: 'Affects renovation possibilities and development potential',
              source: 'Cantonal heritage office',
              title: 'Protected Status',
            },
            renovation: {
              calculation: 'Taken from the year of the last value-increasing renovation in the building register',
              description: "Year of the last renovation that significantly increased the building's value.",
              importance: 'Shows whether the building has been brought up to date since it was built',
              limitations: 'NO LONGER UPDATED IN THE GWR SINCE 2017.',
              source: 'GWR',
              title: 'Last Renovation',
            },
            status: {
              calculation: 'Recorded in building registry and updated based on construction or demolition permits',
              description: 'The current operational status of the building',
              importance: 'Confirms if the building exists, is under construction, or has been demolished',
              source: 'GWR',
              title: 'Current Building Status',
            },
          },
        },
        statusCondition: {
          title: 'Status & Condition',
        },
      },
      heightTypes: {
        highRise: 'High-rise',
        lowRise: 'Low-rise',
        midRise: 'Mid-rise',
      },
      labels: {
        asl: 'm a.s.l.',
        estimated: 'Estimated',
        period: 'Period',
      },
      metrics: {
        buildingAge: {
          label: 'Age',
          tooltip: {
            calculation: 'Calculated as current year minus the construction year from official building registry',
            calculationEstimated: 'Estimated based on construction period {{period}}. The midpoint of the period range is used for calculation',
            description: 'The number of years since the building was originally constructed',
            importance: 'Key factor for assessing maintenance needs, renovation potential, energy efficiency, and property valuation',
            limitations: 'Does not account for major renovations or reconstructions that may have significantly updated the building. May be estimated when the exact year of the building is unknown or a period (e.g: 1946-1960)',
            limitationsEstimated: 'Age is estimated from construction period and may not be exact. Actual construction year may vary within the period range',
            source: 'GWR, Popety.io',
            title: 'Building Age',
          },
        },
        buildingArea: {
          label: 'Area',
          tooltip: {
            calculation: 'Measured from official cadastral data or building registry as the horizontal projection of the building envelope',
            description: 'The ground-level area of the building',
            importance: 'Essential for understanding land use efficiency, calculating floor area ratios, and assessing development potential',
            limitations: 'Does not include external structures like terraces, balconies, or underground areas. May differ from total floor area',
            source: 'Popety.io, GWR',
            title: 'Building Footprint Area',
          },
        },
        buildingHeight: {
          label: 'Height',
          tooltip: {
            calculation: 'Measured from LiDAR data or official building surveys, from ground level to roof ridge or highest structural element',
            description: 'The vertical measurement from ground level to the highest point of the building',
            importance: 'Critical for zoning compliance and assessing development or building extension potential',
            limitations: 'May vary based on measurement method. Antennas, chimneys, and temporary structures may or may not be included',
            source: 'Popety.io, swissSURFACE3D',
            title: 'Total Building Height',
          },
        },
        floorCount: {
          label: 'Floors',
          tooltip: {
            calculation: 'Counted from official building registry data, attics and basements are only counted if they are developed, even partially, for habitation or are heated',
            description: 'The total number of floors and basements in the building, including the ground floor',
            importance: 'Key indicator for building classification, zoning compliance, and estimating gross floor area',
            limitations: 'May not include basement levels, mezzanines, or technical floors, cellars are not taken into consideration.',
            source: 'GWR',
            title: 'Total Floor Count',
          },
        },
        numberOfFloors: {
          label: 'Number of Floors',
        },
      },
      sizeIndicators: {
        compact: 'Compact',
        large: 'Large',
        medium: 'Medium',
      },
      values: {
        asl: 'ASL',
        estimated: '(est.)',
        estimatedFrom: 'Estimated from {{source}}',
        na: 'N/A',
        no: 'No',
        period: '(period)',
        years: 'years',
        yearsAgo: '{{count}} years ago',
        yearsCount: '{{count}} years',
        yes: 'Yes',
      },
    },
    states: {
      error: 'Error: {{error}}',
      loading: 'Loading...',
      noData: 'No data available',
    },
    tabs: {
      neighborhood: 'Neighborhood',
      overview: 'Overview',
      timeline: 'Timeline',
    },
    timeline: {
      ageComparison: {
        comparisonTypes: {
          newerDescription: 'This building was constructed more recently than the neighborhood average, likely with more modern standards',
          newerThanAverage: 'Newer than average',
          olderDescription: 'This building is significantly older than the neighborhood average, which may affect renovation needs and energy efficiency',
          significantlyOlder: 'Significantly older',
          similarAge: 'Similar age',
          similarDescription: 'This building was constructed around the same time as most buildings in the neighborhood',
        },
        labels: {
          comparison: 'Comparison',
          neighborhoodAverage: 'Neighborhood Average',
          thisBuilding: 'This Building',
        },
        newerThanAverage: 'Newer than average',
        significantlyOlder: 'Significantly older',
        similarAge: 'Similar age',
        title: 'Age Comparison',
        tooltip: {
          calculation: 'Based on the construction year of this building compared to the mean construction year of neighboring buildings',
          description: 'Compares the age of this building with the average age of buildings in the surrounding neighborhood',
          importance: 'Understanding how a building age compares to its neighborhood helps assess its relative condition and potential development context',
          limitations: 'Comparison is based on buildings within the analysis radius. Areas with mixed development may show high variance',
          source: 'GWR, Popety.io',
          title: 'Age Comparison with Neighborhood',
        },
        tooltips: {
          comparison: {
            description: 'How this building age compares to the neighborhood average',
            importance: 'Newer buildings may have better energy efficiency; older ones may have character value or need more maintenance',
            source: 'Calculated from building registry data',
            title: 'Age Comparison Result',
          },
          neighborhoodAverage: {
            calculation: 'Mean construction year of all buildings within the analysis radius',
            description: 'The average construction year and age of buildings in the surrounding neighborhood',
            importance: 'Indicates the typical building era for the neighborhood, providing context for the subject building',
            source: 'GWR, Popety.io',
            title: 'Neighborhood Average',
          },
          thisBuilding: {
            calculation: 'Construction year from the official building registry; age calculated as current year minus construction year',
            description: 'The construction year and age of the building being analyzed',
            importance: 'Reference point for neighborhood age comparison',
            source: 'GWR',
            title: 'This Building',
          },
        },
        yearsOld: 'years old',
      },
      badges: {
        estimatedData: 'Estimated Data',
        historicalData: 'Historical Data',
      },
      cards: {
        ageAnalysis: {
          title: 'Age Analysis',
          tooltips: {
            ageCategory: {
              description: 'Classification of the building based on its age into standardized categories',
              importance: 'Places the building in an age band used for condition and market comparison',
              source: 'GWR, Popety.io',
              title: 'Age Category',
            },
            buildingAge: {
              calculation: 'Calculated as the current year minus the construction year from the official building registry',
              description: 'The number of years since the building was originally constructed',
              importance: 'Key factor for assessing maintenance needs, renovation potential, energy efficiency, and property valuation',
              limitations: 'Does not account for major renovations or reconstructions that may have significantly updated the building. May be estimated when the exact year of the building is unknown or a period (e.g: 1946-1960)',
              source: 'GWR, Popety.io',
              title: 'Building Age',
            },
            buildingPeriod: {
              calculation: 'Derived from the building registry when an exact construction year is not available',
              description: 'The construction era or period range when the building was built',
              importance: 'Provides context about construction standards and materials typical of that era',
              title: 'Building Period',
            },
            constructionPeriod: {
              calculation: 'Derived from the construction year and the standard construction-period bands',
              source: 'GWR',
            },
            constructionYear: {
              calculation: 'Taken from the official construction year in the building register',
              importance: 'Anchors the construction timeline and the age of the building',
              limitations: 'For older buildings, may be estimated from construction period range. Major reconstructions may not update this date',
              source: 'GWR',
            },
          },
        },
        ageComparison: {
          title: 'Age Comparison with Neighborhood',
        },
        constructionPhases: {
          title: 'Construction Phases',
          tooltips: {
            constructionDates: {
              calculation: 'Extracted from the building registry construction history records',
              description: 'The specific years when construction or expansion phases were completed',
              importance: 'Shows when major changes occurred, helping understand building evolution and mixed construction standards',
              source: 'GWR',
              title: 'Construction Dates',
            },
            currentStatus: {
              importance: 'Confirms whether the building is operational, under construction, or demolished in the context of its construction history',
              source: 'GWR',
            },
            numberOfPhases: {
              calculation: 'Count of distinct construction dates recorded in the building registry',
              description: 'How many distinct construction or expansion phases the building has undergone',
              importance: 'Multiple phases may indicate extensions or additions that affect building structure and value',
              source: 'GWR',
              title: 'Number of Construction Phases',
            },
            singlePhase: {
              calculation: 'No additional construction dates found in the building registry',
              description: 'The building was constructed in a single phase without subsequent expansions',
              importance: 'Single-phase buildings typically have more uniform construction standards throughout',
              source: 'GWR',
            },
          },
        },
        constructionTimeline: {
          title: 'Construction Timeline',
        },
        renovationHistory: {
          title: 'Renovation History',
          tooltips: {
            lastRenovation: {
              calculation: 'Taken from the last value-increasing renovation year in the building register',
              description: "Year of the last renovation that significantly increased the building's value",
              importance: 'Shows how recently the building received a value-increasing renovation',
              limitations: 'NO LONGER UPDATED IN THE GWR SINCE 2017.',
              source: 'GWR',
            },
            period: {
              calculation: 'Derived from renovation permit records in the building registry',
              description: 'The era or period range when the renovation was completed',
              importance: 'Provides context about the type and quality of renovation work performed',
              title: 'Renovation Period',
            },
            renovationPeriod: {
              calculation: 'Derived from the last renovation year and the standard renovation-period bands',
              importance: 'Places the renovation in a period used to compare work of a similar era',
              limitations: 'NO LONGER UPDATED IN THE GWR SINCE 2017.',
              source: 'GWR',
            },
            renovationStatus: {
              limitations: 'NO LONGER UPDATED IN THE GWR SINCE 2017.',
              source: 'Popety.io',
            },
            status: {
              description: 'Assessment of the building renovation needs based on time since last renovation',
              importance: 'Quick indicator of whether renovation should be planned in the near future',
              title: 'Renovation Status',
            },
            yearsSince: {
              calculation: 'Current year minus the last renovation year from the building registry',
              description: "The number of years since the last renovation that significantly increased the building's value",
              importance: 'Flags buildings whose last value-increasing renovation is no longer recent',
              limitations: 'NO LONGER UPDATED IN THE GWR SINCE 2017.',
              source: 'GWR',
              title: 'Years Since Renovation',
            },
          },
        },
        timeline: {
          title: 'Construction Timeline',
          tooltip: {
            calculation: 'Based on official construction dates, renovation records, and building registry data',
            description: "Visual representation of the building's construction history and major events",
            importance: 'Summarises construction and renovation milestones on one timeline',
            limitations: 'Some dates may be estimated from construction periods when exact years are not available, and some events may be missing',
            source: 'GWR, Popety.io',
            title: 'Construction Timeline',
          },
        },
      },
      chart: {
        tooltipAge: 'Age',
        tooltipYear: 'Year',
        xAxisLabel: 'Year',
        yAxisLabel: 'Building',
      },
      events: {
        constructionPhase: 'Construction Phase',
        dwellingAdded: 'Dwelling Added',
        majorRenovation: 'Major Renovation',
        originalConstruction: 'Original Construction',
        originalConstructionEst: 'Original Construction (Est.)',
        present: 'Present',
      },
      labels: {
        ageCategory: 'Age Category',
        buildingAge: 'Building Age',
        buildingPeriod: 'Building Period',
        comparison: 'Comparison',
        constructionDates: 'Construction Dates',
        constructionYear: 'Construction Year',
        lastRenovation: 'Last Renovation',
        neighborhoodAverage: 'Neighborhood Average',
        noRecord: 'No record',
        numberOfPhases: 'Number of Phases',
        phases: 'phases',
        renovationPeriod: 'Renovation Period',
        renovationStatus: 'Renovation Status',
        singlePhaseConstruction: 'Single Phase Construction',
        thisBuilding: 'This Building',
        yearsSinceRenovation: 'Years Since Renovation',
      },
      noData: 'No timeline data available',
      noDataMessage: 'No construction timeline data available',
      phaseLabels: {
        noRecord: 'No record',
        singlePhase: 'Single phase construction',
      },
      renovationCategories: {
        dueSoon: {
          description: 'Building may benefit from renovation or modernization in the near future',
          label: 'Due Soon',
          range: '15-30 years',
        },
        good: {
          description: 'Renovation is relatively recent; building should still be in good condition',
          label: 'Good Condition',
          range: '5-15 years',
        },
        overdue: {
          description: 'Significant time has passed since last renovation; major renovation work is likely needed',
          label: 'Overdue',
          range: '> 30 years',
        },
        recentlyRenovated: {
          description: 'Building was renovated within the last 5 years and is likely in excellent condition',
          label: 'Recently Renovated',
          range: '< 5 years',
        },
      },
      renovationStatuses: {
        considerUpdates: 'Consider Updates',
        goodCondition: 'Good Condition',
        noRenovation: 'No Renovation',
        recentlyRenovated: 'Recently renovated',
        renovationNeeded: 'Renovation Needed',
      },
    },
  },
  gwr: {
    buildingCategory: {
      "Bâtiment à usage partiel d'habitation": 'Building with partial residential use',
      "Bâtiment d'habitation avec usage annexe": 'Residential building with ancillary use',
      "Bâtiment exclusivement à usage d'habitation": 'Building exclusively for residential use',
      "Bâtiment partiellement à usage d'habitation": 'Building partially for residential use',
      "Bâtiment sans usage d'habitation": 'Building without residential use',
      'Construction particulière': 'Special construction',
      'Habitation provisoire': 'Temporary housing',
    },
    buildingClass: {
      'Autre bâtiment non classé ailleurs': 'Other building not classified elsewhere',
      "Autres bâtiments d'exploitation agricole": 'Other agricultural buildings',
      "Autres bâtiments d'hébergement de tourisme": 'Other tourism accommodation buildings',
      'Autres bâtiments non classés ailleurs': 'Other buildings not classified elsewhere',
      "Autres bâtiments pour l'hébergement collectif": 'Other collective accommodation buildings',
      "Autres bâtiments pour l'hébergement de tourisme": 'Other tourism accommodation buildings',
      "Bâtiment à usage d'habitation non déterminé": 'Residential building, unspecified',
      'Bâtiment à usage mixte': 'Mixed-use building',
      'Bâtiment administratif': 'Administrative building',
      'Bâtiment commercial': 'Commercial building',
      "Bâtiment d'exploitation agricole": 'Agricultural building',
      'Bâtiment de culte': 'Place of worship',
      'Bâtiment industriel': 'Industrial building',
      'Bâtiment pour les transports et les communications': 'Transport and communications building',
      'Bâtiment scolaire': 'School building',
      'Bâtiments administratifs': 'Administrative buildings',
      'Bâtiments commerciaux': 'Commercial buildings',
      "Bâtiments d'exploitation agricole": 'Agricultural buildings',
      'Bâtiments de culte': 'Places of worship',
      'Bâtiments industriels': 'Industrial buildings',
      "Bâtiments pour la garde d'animaux": 'Animal shelter buildings',
      'Bâtiments pour les cultures végétales': 'Plant cultivation buildings',
      'Bâtiments scolaires': 'School buildings',
      Garage: 'Garage',
      Garages: 'Garages',
      'Gares, aérogares, centraux téléphoniques': 'Stations, air terminals, telephone exchanges',
      'Habitat communautaire': 'Communal housing',
      'Hôpital et établissement de santé': 'Hospital and healthcare facility',
      'Hôpitaux et établissements de santé': 'Hospitals and healthcare facilities',
      Hôtel: 'Hotel',
      Hôtels: 'Hotels',
      'Immeuble à trois logements et plus': 'Building with three or more dwellings',
      'Maison à deux logements': 'Two-dwelling house',
      'Maison individuelle': 'Single-family house',
      'Monument historique ou classé': 'Historic or listed monument',
      'Monuments historiques ou classés': 'Historic or listed monuments',
      'Réservoir, silo et entrepôt': 'Reservoir, silo and warehouse',
      'Réservoirs, silos et entrepôts': 'Reservoirs, silos and warehouses',
      'Salle de sport': 'Sports hall',
      'Salle de tir': 'Shooting range',
      'Salles de sport': 'Sports halls',
      'Salles de tir': 'Shooting ranges',
    },
    buildingStatus: {
      autorisé: 'Approved',
      démoli: 'Demolished',
      'en construction': 'Under construction',
      existant: 'Existing',
      inutilisable: 'Unusable',
      'non réalisé': 'Not realized',
      projeté: 'Planned',
    },
    energySource: {
      Air: 'Air',
      Aucun: 'None',
      Aucune: 'None',
      Autre: 'Other',
      'Bois (bois déchiqueté, copeaux)': 'Wood (shredded wood, chips)',
      'Bois (bûches)': 'Wood (log)',
      'Bois (générique)': 'Wood (generic)',
      'Bois (granulés - pellets)': 'Wood (pellets)',
      'Capteurs solaires thermiques': 'Solar thermal collectors',
      'Chaleur produite à distance (basse température)': 'Remote heat production (low temperature)',
      'Chaleur produite à distance (générique)': 'Remote heat generation (generic)',
      'Chaleur produite à distance (haute température)': 'Remote heat production (high temperature)',
      'Eau (nappe phréatique, eaux superficielles, eaux résiduelles)': 'Water (groundwater, surface water, wastewater)',
      Electricité: 'Electricity',
      Gaz: 'Gas',
      Indéterminé: 'Undetermined',
      Mazout: 'Fuel oil',
      'Pas de générateur': 'No generator',
      'Pompe à chaleur': 'Heat pump',
      'Rejets thermiques (dans le bâtiment)': 'Thermal discharges (in the building)',
      'Serpentin géothermique': 'Geothermal heat exchanger',
      'Soleil (thermique)': 'Sun (thermal)',
      'Sonde géothermique': 'Geothermal probe',
    },
    heaterGenerator: {
      Autre: 'Other',
      'Boiler électrique central': 'Central electric boiler',
      'Chaudière (générique)': 'Boiler (generic)',
      'Chaudière (générique) pour plusieurs bâtiments': 'Boiler (generic) for multiple buildings',
      'Chaudière (générique) pour un seul bâtiment': 'Boiler (generic) for single building',
      'Chaudière à condensation': 'Condensing boiler',
      'Chaudière à condensation pour plusieurs bâtiments': 'Condensing boiler for multiple buildings',
      'Chaudière à condensation pour un seul bâtiment': 'Condensing boiler for single building',
      'Chaudière standard': 'Standard boiler',
      'Chaudière standard pour plusieurs bâtiments': 'Standard boiler for multiple buildings',
      'Chaudière standard pour un seul bâtiment': 'Standard boiler for single building',
      'Chauffage central électrique': 'Central electric heating',
      'Chauffage central électrique pour plusieurs bâtiments': 'Central electric heating for multiple buildings',
      'Chauffage central électrique pour un seul bâtiment': 'Central electric heating for single building',
      'Chauffage électrique direct (y compris radiateur infra-rouge)': 'Direct electric heating (incl. infrared)',
      'Échangeur de chaleur (y compris pour la chaleur à distance)': 'Heat exchanger (including district heating)',
      'Echangeur de chaleur (y.c. chauffage à distance)': 'Heat exchanger (incl. district heating)',
      'Echangeur de chaleur (y.c. chauffage à distance) pour plusieurs bâtiments': 'Heat exchanger for multiple buildings',
      'Echangeur de chaleur (y.c. chauffage à distance) pour un seul bâtiment': 'Heat exchanger for single building',
      Indéterminé: 'Undetermined',
      'Installation couplage chaleur-force': 'CHP installation',
      'Installation couplage chaleur-force pour plusieurs bâtiments': 'CHP for multiple buildings',
      'Installation couplage chaleur-force pour un seul bâtiment': 'CHP for single building',
      'Installation solaire thermique': 'Solar thermal installation',
      'Installation solaire thermique pour plusieurs bâtiments': 'Solar thermal for multiple buildings',
      'Installation solaire thermique pour un seul bâtiment': 'Solar thermal for single building',
      'Pas de générateur de chaleur': 'No heat generator',
      Poêle: 'Stove',
      'Pompe à chaleur PAC': 'Heat pump',
      'Pompe à chaleur PAC pour plusieurs bâtiments': 'Heat pump for multiple buildings',
      'Pompe à chaleur PAC pour un seul bâtiment': 'Heat pump for single building',
    },
  },
  ui: {
    exportPdf: {
      unavailable: 'This PDF export is unavailable. Close the preview and try again.',
    },
    aiFilter: {
      analyzing: 'Analyzing your request...',
      apply: 'Apply',
      clearHistory: 'Clear',
      description: "Describe what you're looking for in natural language, and I'll apply the appropriate filters.",
      errors: {
        microphoneAccess: 'Unable to access microphone. Please check your permissions.',
        textProcessing: 'An error occurred while processing your request',
        voiceNotConfigured: 'Voice input is not configured for this filter.',
        voiceProcessing: 'An error occurred while processing your voice request',
      },
      examplePrompts: {
        buildings: 'Properties built after 2020 with parking',
        properties: 'Show me properties over 100m² in city center',
        transactions: 'Find recent transactions above 1 million',
      },
      filtersApplied: 'Filters Applied',
      filterSaved: 'Filter saved',
      placeholder: 'Type or speak your filter request...',
      recentSearches: 'Recent Searches',
      recording: 'Recording...',
      saveAsFilter: 'Save as filter',
      startRecording: 'Start voice recording',
      stopRecording: 'Stop recording',
      title: 'AI Filter Assistant',
      trySaying: 'Examples:',
    },
    analysisMap: {
      baseLayers: {
        building: 'Building',
        building3d: 'Building 3D',
        buildingNumbers: 'Building Numbers',
        chooseMapStyle: 'Choose Map Style',
        contours: 'Contours',
        dimensionMetrics: 'Dimension Metrics',
        distanceLimit: 'Distance Limit',
        footprint: 'Footprint',
        labels: 'Labels',
        land: 'Land',
        mapBase: 'Map Base',
        mapFeatures: 'Map Features',
        parcelBoundaries: 'Parcel Boundaries',
        pointCloud: 'Point cloud',
        streets: 'Streets',
        terrain: 'Terrain',
      },
      controls: {
        hideMap: 'Hide map',
      },
      groupLabels: {
        demographicsLayer: 'Demographics Layer',
        employmentLayer: 'Employment Layer',
        historicalImagery: 'Historical Imagery',
        noiseLayer: 'Noise Layer',
        purchaseMarketLayer: 'Purchase Market Layer',
        rentalMarketLayer: 'Rental Market Layer',
        zoningLayer: 'Zoning Layer',
      },
      groupTitles: {
        amenities: 'Amenities',
        buildingPermits: 'Building Permits',
        demographics: 'Demographics',
        dynamicLayers: 'Dynamic Layers',
        employment: 'Employment',
        energy: 'Energy',
        historicalSatelliteImages: 'Historical Satellite Images',
        layers: 'Layers',
        naturalHazards: 'Natural Hazards',
        noiseAnalysis: 'Noise Analysis',
        purchaseListings: 'Purchase Listings',
        purchaseMarket: 'Purchase Market',
        rentalListings: 'Rental Listings',
        rentalMarket: 'Rental Market',
        restrictions: 'Restrictions',
        sunlightStudy: 'Sunlight Study',
        transactions: 'Transactions',
        transportAccess: 'Transport & Access',
        zoning: 'Zoning',
        zoningRules: 'Zoning Rules',
      },
      hazardTypes: {
        avalanche: 'Avalanche',
        flood: 'Flood',
        landslide: 'Landslide',
        rockslide: 'Rockslide',
        title: 'Hazard Types',
      },
      layers: {
        amenitiesCulture: 'Culture',
        amenitiesCultureDescription: 'Museums, theaters, cultural venues',
        amenitiesDining: 'Dining',
        amenitiesDiningDescription: 'Restaurants, cafes, bars',
        amenitiesEducation: 'Education',
        amenitiesEducationDescription: 'Schools, universities, libraries',
        amenitiesGrocery: 'Grocery',
        amenitiesGroceryDescription: 'Supermarkets, grocery stores',
        amenitiesHealthcare: 'Healthcare',
        amenitiesHealthcareDescription: 'Hospitals, clinics, pharmacies',
        amenitiesServices: 'Services',
        amenitiesServicesDescription: 'Banks, post offices, public services',
        amenitiesShopping: 'Shopping',
        amenitiesShoppingDescription: 'Shopping centers, malls, retail stores',
        amenitiesTransport: 'Transport',
        amenitiesTransportDescription: 'Public transport stops and stations',
        amenitiesWellness: 'Wellness',
        amenitiesWellnessDescription: 'Gyms, spas, wellness centers',
        buildingPermitsMarkers: 'Building Permits',
        buildingPermitsMarkersDescription: 'Show building permit locations on the map',
        cyclingTravelTime: 'Cycling Travel Time',
        cyclingTravelTimeDescription: '5, 10, 15, 30 minute cycling zones',
        demographicsAgeGroup: 'Dominant Age Group',
        demographicsAgeGroupDescription: 'Dominant demographic segment for targeted marketing',
        demographicsElderlyPct: 'Elderly (65+)',
        demographicsElderlyPctDescription: 'Accessible housing and healthcare proximity demand',
        demographicsForeignPct: 'Foreign Population',
        demographicsForeignPctDescription: 'International market demand indicator',
        demographicsGrowthRate: 'Population Growth (5-year)',
        demographicsGrowthRateDescription: 'Market momentum and investment potential',
        demographicsHouseholdType: 'Dominant Household Type',
        demographicsHouseholdTypeDescription: 'Housing unit size demand (studios vs family homes)',
        demographicsIsochrone: 'Search Area',
        demographicsIsochroneDescription: 'Shows the travel time area used for demographic analysis',
        demographicsPopulationDensity: 'Population Density',
        demographicsPopulationDensityDescription: 'Core urbanization indicator for real estate demand',
        demographicsResidenceDuration: 'Dominant Residence Duration',
        demographicsResidenceDurationDescription: 'Neighborhood stability and turnover indicator',
        demographicsYoungAdultPct: 'Young Adults (20-39)',
        demographicsYoungAdultPctDescription: 'Rental market strength and starter home demand',
        drivingTravelTime: 'Driving Travel Time',
        drivingTravelTimeDescription: '5, 10, 15, 30 minute driving zones',
        employmentDensity: 'Employment Density',
        employmentDensityDescription: 'Commercial real estate demand and economic activity indicator',
        employmentDominantSector: 'Dominant Employment Sector',
        employmentDominantSectorDescription: 'Economic character (Primary/Secondary/Tertiary)',
        employmentFulltimeRatio: 'Full-Time Employment Ratio',
        employmentFulltimeRatioDescription: 'Income stability and residential demand strength',
        employmentGrowthRate: 'Employment Growth (5-year)',
        employmentGrowthRateDescription: 'Market momentum and investment potential',
        employmentIsochrone: 'Search Area',
        employmentIsochroneDescription: 'Shows the travel time area used for employment analysis',
        employmentJobsPerEstablishment: 'Jobs per Establishment',
        employmentJobsPerEstablishmentDescription: 'Business size indicator (corporate vs SME)',
        employmentPublicPrivate: 'Public vs Private Sector',
        employmentPublicPrivateDescription: 'Government presence and tenant stability',
        listingsMarkers: 'Listing Markers',
        listingsMarkersDescription: 'Show individual property listings on the map',
        nearbyBuildings: 'Nearby Buildings',
        noiseRailwayDay: 'Railway Noise - Day',
        noiseRailwayDayDescription: 'Railway noise levels during daytime in dB',
        noiseRailwayNight: 'Railway Noise - Night',
        noiseRailwayNightDescription: 'Railway noise levels during nighttime in dB',
        noiseRoadDay: 'Road Noise - Day',
        noiseRoadDayDescription: 'Road noise levels during daytime in dB',
        noiseRoadNight: 'Road Noise - Night',
        noiseRoadNightDescription: 'Road noise levels during nighttime in dB',
        purchaseMarketApartmentPrice: 'Avg Purchase Price/m² - Apartment',
        purchaseMarketApartmentPriceDescription: 'Average purchase price per square meter for apartments in the area',
        purchaseMarketHousePrice: 'Avg Purchase Price/m² - House',
        purchaseMarketHousePriceDescription: 'Average purchase price per square meter for houses in the area',
        rentalMarketApartmentPrice: 'Avg Rental Price/m² - Apartment',
        rentalMarketApartmentPriceDescription: 'Average rental price per square meter for apartments in the area',
        rentalMarketHousePrice: 'Avg Rental Price/m² - House',
        rentalMarketHousePriceDescription: 'Average rental price per square meter for houses in the area',
        restrictionsBase: 'Restrictions',
        restrictionsBaseDescription: 'Land use restrictions and constraints',
        shadowSimulation: 'Shadow Simulation',
        shadowSimulationDescription: 'Real-time shadow simulation based on date and time',
        solarPotential: 'Solar Potential',
        solarPotentialDescription: 'Roof sections colored by solar potential (kWh/m²/year)',
        sunDirection: 'Sun Direction',
        sunDirectionDescription: 'Shows sunrise, sunset, and current sun direction as lines from map center',
        sunlightHours: 'Sunlight Hours',
        sunlightHoursDescription: 'Color-coded visualization showing total hours of sun exposure per day from sunrise to sunset',
        transactionPoints: 'Transaction Points',
        transactionPointsDescription: 'Show historical real estate transactions',
        transportBusStops: 'Bus Stops',
        transportBusStopsDescription: 'Public bus stops within 1.5km radius',
        transportEvCharging: 'EV Charging Stations',
        transportEvChargingDescription: 'Electric vehicle charging stations within 5km',
        transportHighwayAccess: 'Highway Access',
        transportHighwayAccessDescription: 'Highway exits and entrances within 15km',
        transportTrainStops: 'Train Stations',
        transportTrainStopsDescription: 'Train stations within 1.5km radius',
        transportTramStops: 'Tram Stops',
        transportTramStopsDescription: 'Tram stops within 1.5km radius',
        walkingTravelTime: 'Walking Travel Time',
        walkingTravelTimeDescription: '5, 10, 15, 30 minute walking zones',
        zoningAll: 'All Zoning Types',
        zoningAllDescription: 'Display all zoning types (General, Cantonal, Local, Partial)',
        zoningCantonalOnly: 'Cantonal Only',
        zoningCantonalOnlyDescription: 'Display only cantonal zoning zones',
        zoningGeneralOnly: 'General Only',
        zoningGeneralOnlyDescription: 'Display only general zoning zones',
        zoningLocalOnly: 'Local Only',
        zoningLocalOnlyDescription: 'Display only local zoning zones',
        zoningMapStyle: 'Zoning Map',
        zoningMapStyleDescription: 'Display zoning on standard map',
        zoningPartialOnly: 'Partial Only',
        zoningPartialOnlyDescription: 'Display only partial zoning zones',
        zoningSatelliteMapStyle: 'Satellite Zoning Map',
        zoningSatelliteMapStyleDescription: 'Display zoning on satellite imagery',
      },
      layersPanel: {
        closePanel: 'Close layers panel',
        disabledSelectLayer: 'Disabled - Select a layer',
        disableLayer: 'Disable the layer',
        hideAll: 'Hide All',
        opacity: 'Opacity',
        searchLayers: 'Search layers',
        searchPlaceholder: 'Search layers...',
        selectLayer: 'Select...',
        showAll: 'Show All',
        title: 'Map Layers',
      },
      legends: {
        cyclingTravelTime: 'Cycling Travel Time',
        drivingTravelTime: 'Driving Travel Time',
        min: '{{count}} min',
        walkingTravelTime: 'Walking Travel Time',
      },
      mapStyles: {
        light: 'Light',
        lightDescription: 'Minimal style for data visualization',
        outdoors: 'Outdoors',
        outdoorsDescription: 'Outdoor map with terrain and hiking paths',
        popetyio: 'Popety.io',
        popetyioDescription: 'Popety.io map style for Switzerland',
        satellite: 'Satellite',
        satelliteDescription: 'Aerial imagery with street overlay',
        standard: 'Standard',
        standardDescription: '3D Building street map style',
        traffic: 'Traffic',
        trafficDescription: 'Map with traffic conditions',
        zoning: 'Zoning',
        zoningDescription: 'Custom style with zoning colors',
        zoningSatellite: 'Zoning Satellite',
        zoningSatelliteDescription: 'Satellite imagery with zoning overlay',
      },
      restrictionTypes: 'Restriction Types',
      sunDirectionLegend: {
        currentSun: 'Current Sun',
        helpText: 'Lines radiate from map center showing sun direction',
        sunrise: 'Sunrise',
        sunset: 'Sunset',
        title: 'Sun Direction',
      },
      sunlightControls: {
        date: 'Date',
        now: 'Now',
        spring: 'Spring',
        summer: 'Summer',
        sunExposureInfo: 'Showing daily sun exposure from sunrise to sunset',
        time: 'Time',
        timeline: 'Day Timeline',
        today: 'Today',
        winter: 'Winter',
      },
    },
    common: {
      actions: {
        back: 'Back',
        cancel: 'Cancel',
        close: 'Close',
        confirm: 'Confirm',
        delete: 'Delete',
        edit: 'Edit',
        loadMore: 'Load more',
        next: 'Next',
        save: 'Save',
        submit: 'Submit',
        viewDetails: 'View details',
      },
      no: 'No',
      status: {
        error: 'An error occurred',
        loading: 'Loading...',
        saving: 'Saving...',
        success: 'Success',
      },
      validation: {
        invalidEmail: 'Invalid email address',
        invalidFormat: 'Invalid format',
        maxLength: 'Maximum {{count}} characters',
        minLength: 'Minimum {{count}} characters',
        required: 'This field is required',
      },
      yes: 'Yes',
    },
    dataTable: {
      actions: 'Actions',
      bulkActions: {
        exportAsExcel: 'Export as Excel',
        itemsSelected: '{{count}} items selected',
      },
      columns: {
        button: 'Columns',
        dragHint: 'Drag table headers to reorder columns',
        moveColumn: 'Move {{column}} column',
        resetToDefault: 'Reset to Default',
        showHide: 'Show/Hide Columns',
      },
      empty: {
        description: 'Try adjusting your filters or search criteria',
        title: 'No Data Found',
      },
      error: {
        description: 'An error occurred while loading data',
        title: 'Error Loading Data',
      },
      loading: 'Loading data...',
      mobile: {
        pullToRefresh: 'Pull to refresh',
        refreshing: 'Refreshing...',
        releaseToRefresh: 'Release to refresh',
        selectColumn: 'Select column',
        showLess: 'Show less',
        showMore: 'Show more',
        sortAscending: 'Sort ascending',
        sortBy: 'Sort by',
        sortDescending: 'Sort descending',
      },
      pagination: {
        items: 'items',
        of: 'of',
        show: 'Show',
        showing: 'Showing',
      },
    },
    detailPanel: {
      close: 'Close',
      closePanel: 'Close panel',
      viewDetails: 'View details',
    },
    drawingTools: {
      '3dMode': '3D Mode',
      deleteAll: 'Delete All',
      deleteSelected: 'Delete Selected ({{count}})',
      drawLine: 'Draw Line',
      drawPolygon: 'Draw Polygon',
      measureDistance: 'Measure Distance',
      print: 'Print map',
      tooltip: 'Drawing Tools',
    },
    files: {
      categories: {
        cadFiles: 'CAD Files',
        documents: 'Documents',
        images: 'Images',
        other: 'Other',
        pdfDocuments: 'PDF Documents',
        spreadsheets: 'Spreadsheets',
      },
      confirmDelete: 'Delete this file?',
      deleteFile: 'Delete file',
      downloadFile: 'Download file',
      emptyDescription: 'Upload files to keep important documents organized',
      emptyTitle: 'No files uploaded',
      retry: 'Retry',
      searchPlaceholder: 'Search files...',
      searchResults: '{{count}} result of {{total}} items',
      searchResultsPlural: '{{count}} results of {{total}} items',
      time: {
        daysAgo: '{{count}}d ago',
        hoursAgo: '{{count}}h ago',
        justNow: 'Just now',
        lastWeek: 'Last week',
        minutesAgo: '{{count}} min ago',
        weeksAgo: '{{count}} weeks ago',
        yesterday: 'Yesterday',
      },
      title: 'Files',
      uploadFile: 'Upload file',
      uploadHint: 'PDF, DOC, XLS, DWG, Images (max {{maxSize}}MB)',
      uploadText: 'Click to upload or drag files here',
    },
    filters: {
      filterContainer: {
        clearAll: 'Clear all',
        hideFilters: 'Hide filters',
        lessFilters: 'Less filters',
        loading: 'Loading filters...',
        moreFilters: 'More filters',
        resultsFound: '{{count}} results found',
        showFilters: 'Show filters',
      },
      filterPanel: {
        apply: 'Apply',
        clear: 'Clear',
        emptyState: 'Filter options will be displayed here',
        filters: 'Filters',
        invalidRange: 'Min must be lower than max — results are not updated',
        reset: 'Reset',
        resetFilters: 'Reset filters',
        toggleFilters: 'Toggle filters',
      },
      quickFilters: {
        activeCount_zero: 'No filters active',
        activeCount_one: '{{count}} filter active',
        activeCount_other: '{{count}} filters active',
        activeFilters: 'Active Filters',
        removeFilter: 'Remove filter {{label}}',
        reset: 'Reset',
        save: 'Save filter',
        viewLess: 'View less',
        viewMore: '+{{count}} more',
      },
    },
    forgotPassword: {
      backToLogin: 'Back to login',
      description: "Enter your email address and we'll send you a link to reset your password.",
      emailLabel: 'Email Address',
      emailPlaceholder: 'Enter your email',
      emailRequired: 'Email is required',
      errorMessage: 'An error occurred. Please try again.',
      invalidEmail: 'Please enter a valid email address',
      sending: 'Sending...',
      sendLink: 'Send reset link',
      submitButton: 'Send New Password',
      success: 'Check your email for a link to reset your password.',
      successMessage: 'If the email exists, a new password has been sent to your email address.',
      title: 'Reset Password',
    },
    locationFilter: {
      clear: 'Clear location',
      noResults: 'No locations found',
      placeholder: 'Search location...',
      types: {
        address: 'Address',
        district: 'District',
        municipality: 'Municipality',
        postalCode: 'Postal Code',
        region: 'Region',
      },
    },
    login: {
      continueWithGoogle: 'Continue with Google',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      orContinueWith: 'Or continue with',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      rememberMe: 'Remember me',
      signIn: 'Sign in',
      signingIn: 'Signing in...',
      signUp: 'Sign up',
      title: 'Sign in',
    },
    map: {
      controls: {
        flyToLocation: 'Fly to my location',
        flyToProperty: 'Fly to property',
        fullscreen: 'Fullscreen',
        myLocation: 'My location',
        resetNorth: 'Reset to north',
        resetView: 'Reset view',
        showMap: 'Show map',
        showSatellite: 'Show satellite',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
      },
      layers: {
        map: 'Map',
        satellite: 'Satellite',
        streets: 'Streets',
        terrain: 'Terrain',
      },
      vectorTile: {
        error: 'Error loading map data: {{error}}',
        featuresAtLocation: '{{count}} features at this location',
        listingsAtLocation: '{{count}} listings at this location',
        loading: 'Loading map data...',
        overlapCount: '{{count}} more. Click to see all',
        scrollFeatures: 'Scroll to browse all {{count}} items',
        scrollListings: 'Scroll to browse all {{count}} listings',
      },
    },
    metricSourceModal: {
      agreement: {
        conflict: 'Conflict — 2 sources disagree on this value',
        userModified: 'User modified — Original value from {{source}}: {{value}}',
      },
      badges: {
        estimate: 'EST',
        geoportal: 'GEO',
        llm: 'REG',
        manual: 'EDIT',
        old: 'OLD',
      },
      boundSelector: {
        ariaLabel: 'Select min or max',
        max: 'MAX',
        min: 'MIN',
      },
      estimate: {
        dotStripLabel: 'Distribution of {{metric}} across {{count}} parcels — larger dots = more parcels',
        dotTooltip: '{{count}} parcel(s) in range',
        legendEstimate: 'Estimated value ({{value}})',
        legendMedian: 'Median ({{value}})',
        legendParcel: '1 parcel',
        legendParcelScale: 'Fewer / more parcels',
        loading: 'Loading statistics...',
        noBuildings: 'No buildings found in this zone to compute statistics',
        rangeText: 'Values in this zone range from {{min}} to {{max}} (median: {{median}})',
        step1Desc: '{{buildingCount}} buildings across {{landCount}} land parcels were used for the calculation',
        step1Title: 'We analyzed the existing buildings in this zone',
        step2Desc: {
          cos: 'Building footprint area divided by land area on each parcel',
          cus: 'Total floor area divided by land area on each parcel',
          floor_nb: 'Maximum number of floors per building on each parcel',
          height: 'Maximum building height on each parcel',
          ibus: 'Total building area divided by land area on each parcel',
          im: 'Building mass (volume) relative to land area',
          unit_nb: 'Maximum number of units per building on each parcel',
          vbr: 'Building volume divided by land area on each parcel',
        },
        step2Title: '{{metric}} was computed for each parcel',
        step3Desc: '{{percentile}}% of parcels have a {{metric}} at or below {{value}}',
        step3Title: 'The value reflects what most parcels allow',
        title: 'How was this value calculated?',
      },
      geoportal: {
        openTab: 'Open in new tab',
        title: 'Cantonal Geoportal',
        viewLink: 'View on cantonal geoportal',
      },
      labels: {
        confirmedValue: 'Confirmed value',
        displayedValue: 'Displayed value',
        maximumValue: 'Maximum Value',
        minimumValue: 'Minimum Value',
        userValue: 'User value',
        value: 'Value',
      },
      noData: {
        message: 'No value available from any source for this metric.',
      },
      noDocument: {
        message: 'No reference document available for this source.',
      },
      reference: {
        article: 'Article',
        articlePath: 'Article Path',
        document: 'Document',
        documentTitle: 'Document Title',
        pages: 'Page(s)',
        viewPdf: 'View PDF',
      },
      report: {
        cancel: 'Cancel',
        contextTitle: 'Value details',
        error: 'Failed to send report. Please try again.',
        explanation: 'What is incorrect?',
        explanationPlaceholder: 'Describe what is wrong and the correct information...',
        link: 'Report an incorrect value',
        submit: 'Send report',
        success: 'Report sent successfully. Thank you!',
        suggestedValue: 'Correct value (optional)',
        suggestedValuePlaceholder: 'Enter the correct value',
        title: 'Report incorrect value',
      },
      sections: {
        additionalInfo: 'Additional Information',
        maxAdditionalInfo: 'Maximum Value - Additional Information',
        minAdditionalInfo: 'Minimum Value - Additional Information',
        referenceInfo: 'Reference Information',
      },
      sources: {
        estimate: 'Estimated',
        geoportal: 'Geoportal',
        llm: 'Popety.io Research Lab',
        manual: 'Manual Override',
        old: 'Legacy Data',
      },
      status: {
        notApplicable: 'Not Applicable',
        seeReference: 'See reference',
      },
      titleDetails: 'Details',
      titleRange: 'Range',
      tooltips: {
        estimate: 'Estimated from Popety.io Algorithms',
        geoportal: 'Official geoportal',
        llm: 'Extracted from regulation',
        manual: 'Replaced by user',
        old: 'Legacy data',
      },
      userOverride: {
        description: 'This value has been manually modified by the user. Click the {{source}} tab to see the original source details.',
        noAuthoritativeSource: 'No authoritative source (regulation or geoportal) was available for this metric.',
        timeline: {
          step1Desc: 'Source {{source}} : {{value}}',
          step1NoSource: 'No original source available',
          step1Title: 'Value estimated by algorithm',
          step1TitleGeo: 'Value from geoportal',
          step1TitleReg: 'Value from regulation',
          step2Desc: 'The {{source}} value ({{originalValue}}) was manually replaced',
          step2DescNoSource: 'A manual value was set by the user',
          step2Title: 'User modified the value to {{value}}',
          step3Desc: 'Difference: {{diff}} compared to the estimate',
          step3DescNoOriginal: 'Manually set value',
          step3Title: 'The value used is {{value}}',
          title: 'How was this value defined?',
        },
      },
      warnings: {
        estimate: 'This value has been estimated using our internal model based on similar properties in the area. Please note that this is an estimate and may not reflect the actual authorized value. For more accurate information, please refer to the official zoning documents or contact the local authorities.',
        estimateShort: "This value has been estimated by Popety.io's algorithms using spatial analysis on plots in the same zone. It may differ from official zoning documents. Always verify with local authorities.",
        geoportalShort: 'This value comes from the official cantonal geoportal. Discrepancies may occur if regulations have recently changed. Please verify with official zoning documents or local authorities.',
        llm: "This value has been researched and provided by Popety.io's Research Lab using advanced algorithms and manual work. While we strive for accuracy, please verify this information with official zoning documents or local authorities to ensure compliance.",
        regulationShort: "This value has been researched and provided by Popety.io's Research Lab using advanced algorithms and manual work. While we strive for accuracy, please verify with official zoning documents or local authorities.",
      },
    },
    pdfViewer: {
      errors: {
        defaultMessage: 'An error occurred while loading the PDF document. Please try again.',
        loadFailed: 'Failed to load PDF',
        noUrl: 'No PDF URL provided',
        pageNotFound: 'Page not found',
        retry: 'Retry',
        tryAgain: 'Try Again',
      },
      loading: {
        text: 'Loading PDF document...',
      },
      search: {
        closeSearch: 'Close search (Esc)',
        nextMatch: 'Next match (Enter)',
        noMatches: 'No matches',
        noResults: 'No results found',
        previousMatch: 'Previous match (Shift+Enter)',
        resultsCount: '{{current}} of {{total}}',
      },
      toolbar: {
        download: 'Download',
        downloadPdf: 'Download PDF',
        exitFullscreen: 'Exit fullscreen',
        fullscreen: 'Fullscreen',
        nextPage: 'Next page',
        of: 'of',
        page: 'Page',
        previousPage: 'Previous page',
        resetZoom: 'Reset zoom',
        rotateLeft: 'Rotate left',
        rotateRight: 'Rotate right',
        search: 'Search',
        searchPlaceholder: 'Search in document...',
        searchShortcut: 'Search (Ctrl+F)',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
      },
    },
    propertyAttributes: {
      categories: {
        amenities: 'Amenities & Features',
        basic: 'Basic Information',
        condition: 'Condition & Quality',
        energy: 'Energy & Systems',
        environment: 'Environment & Location',
        physical: 'Physical Characteristics',
      },
      empty: 'No property attributes available.',
      fields: {
        airConditioning: 'Air Conditioning',
        altitude: 'Altitude',
        attic: 'Attic',
        balcony: 'Balcony Area',
        buildingHeight: 'Building Height',
        cellar: 'Cellar',
        condition: 'Overall Condition',
        constructionYear: 'Construction Year',
        distanceToPowerlines: 'Distance to Powerlines',
        elevator: 'Elevator',
        energyClass: 'Energy Class',
        exposureOrientation: 'Exposure Orientation',
        facadeCondition: 'Facade Condition',
        fireplace: 'Fireplace',
        floor: 'Floor',
        floorArea: 'Floor Area',
        garage: 'Garage Spaces',
        garden: 'Garden Area',
        heatConsumptionIndex: 'Heat Consumption Index',
        heatingSystemAge: 'Heating System Age',
        heatingType: 'Heating Type',
        lakeView: 'Lake View',
        landArea: 'Land Area',
        lastMaintenanceYear: 'Last Maintenance Year',
        livingArea: 'Living Area',
        minergie: 'Minergie Certified',
        mountainView: 'Mountain View',
        noiseLevel: 'Noise Level',
        numberOfBathrooms: 'Bathrooms',
        numberOfBedrooms: 'Bedrooms',
        numberOfFloors: 'Number of Floors',
        parking: 'Parking Spaces',
        pool: 'Swimming Pool',
        propertyType: 'Property Type',
        publicTransportQuality: 'Public Transport Quality',
        renovationYear: 'Renovation Year',
        roofCondition: 'Roof Condition',
        roomsNb: 'Number of Rooms',
        sauna: 'Sauna',
        slope: 'Slope',
        solarArea: 'Solar Panel Area',
        solarPanelsInstalled: 'Solar Panels Installed',
        solarPotential: 'Solar Potential',
        terrace: 'Terrace Area',
        travelTimeToCenters: 'Travel Time to Centers',
        usableArea: 'Usable Area',
        volume: 'Volume',
        windowsCondition: 'Windows Condition',
      },
      units: {
        cubicMeters: 'm³',
        meters: 'm',
        rooms: 'rooms',
        spaces: 'spaces',
        squareMeters: 'm²',
        years: 'years',
      },
    },
    propertyAttributesForm: {
      buttons: {
        cancel: 'Cancel',
        save: 'Save Changes',
        saving: 'Saving...',
      },
      categories: {
        amenities: 'Amenities & Features',
        basic: 'Basic Information',
        condition: 'Condition & Quality',
        energy: 'Energy & Systems',
        environment: 'Environment & Location',
        physical: 'Physical Characteristics',
      },
      fields: {
        airConditioning: 'Air Conditioning',
        altitude: 'Altitude',
        attic: 'Attic',
        balcony: 'Balcony Area',
        buildingHeight: 'Building Height',
        cellar: 'Cellar',
        condition: 'Overall Condition',
        constructionYear: 'Construction Year',
        distanceToPowerlines: 'Distance to Powerlines',
        elevator: 'Elevator',
        energyClass: 'Energy Class',
        exposureOrientation: 'Exposure Orientation',
        facadeCondition: 'Facade Condition',
        fireplace: 'Fireplace',
        floor: 'Floor',
        floorArea: 'Floor Area',
        garage: 'Garage Spaces',
        garden: 'Garden Area',
        heatConsumptionIndex: 'Heat Consumption Index',
        heatingSystemAge: 'Heating System Age',
        heatingType: 'Heating Type',
        lakeView: 'Lake View Quality',
        landArea: 'Land Area',
        lastMaintenanceYear: 'Last Maintenance Year',
        livingArea: 'Living Area',
        minergie: 'Minergie Certified',
        mountainView: 'Mountain View Quality',
        noiseLevel: 'Noise Level',
        numberOfBathrooms: 'Number of Bathrooms',
        numberOfBedrooms: 'Number of Bedrooms',
        numberOfFloors: 'Number of Floors',
        parking: 'Parking Spaces',
        pool: 'Swimming Pool',
        propertyType: 'Property Type',
        publicTransportQuality: 'Public Transport Quality',
        renovationYear: 'Renovation Year',
        roofCondition: 'Roof Condition',
        roomsNb: 'Number of Rooms',
        sauna: 'Sauna',
        slope: 'Terrain Slope',
        solarArea: 'Solar Panel Area',
        solarPanelsInstalled: 'Solar Panels Installed',
        solarPotential: 'Solar Potential',
        terrace: 'Terrace Area',
        travelTimeToCenters: 'Travel Time to Centers',
        usableArea: 'Usable Area',
        volume: 'Volume',
        windowsCondition: 'Windows Condition',
      },
      options: {
        heatingType: {
          districtHeating: 'District Heating',
          electricity: 'Electricity',
          gas: 'Gas',
          heatPump: 'Heat Pump',
          oil: 'Oil',
          other: 'Other',
          pellets: 'Pellets',
          solarThermal: 'Solar Thermal',
          wood: 'Wood',
        },
        level: {
          high: 'High',
          low: 'Low',
          medium: 'Medium',
        },
        quality: {
          bad: 'Bad',
          good: 'Good',
          medium: 'Medium',
        },
        select: 'Select...',
        viewQuality: {
          excellent: 'Excellent',
          good: 'Good',
          none: 'None',
          partial: 'Partial',
        },
      },
      placeholders: {
        altitude: 'e.g., 450',
        balcony: 'e.g., 12',
        buildingHeight: 'e.g., 12',
        condition: 'e.g., excellent, good, fair',
        constructionYear: 'e.g., 1985',
        distanceToPowerlines: 'e.g., 200',
        exposureOrientation: 'e.g., south, south-west',
        facadeCondition: 'e.g., excellent, good, fair',
        floor: 'e.g., 3',
        floorArea: 'e.g., 100',
        garage: 'e.g., 1',
        garden: 'e.g., 200',
        heatConsumptionIndex: 'e.g., 80',
        heatingSystemAge: 'e.g., 10',
        landArea: 'e.g., 500',
        lastMaintenanceYear: 'e.g., 2020',
        livingArea: 'e.g., 120',
        numberOfBathrooms: 'e.g., 2',
        numberOfBedrooms: 'e.g., 3',
        numberOfFloors: 'e.g., 4',
        parking: 'e.g., 2',
        propertyType: 'e.g., apartment, house, villa',
        renovationYear: 'e.g., 2015',
        roofCondition: 'e.g., new, good, needs repair',
        roomsNb: 'e.g., 4.5',
        slope: 'e.g., flat, gentle, steep',
        solarArea: 'e.g., 30',
        terrace: 'e.g., 25',
        travelTimeToCenters: 'e.g., 15 minutes to city center',
        usableArea: 'e.g., 140',
        volume: 'e.g., 450',
        windowsCondition: 'e.g., double glazed, new',
      },
      units: {
        cubicMeters: 'm³',
        meters: 'm',
        squareMeters: 'm²',
        years: 'years',
      },
    },
    propertyDescriptionEditor: {
      buttons: {
        cancel: 'Cancel',
        edit: 'Edit',
        editWithAi: 'Edit with AI',
        save: 'Save',
        saving: 'Saving...',
      },
      characters: 'characters',
      emptyState: 'Click "Edit with AI" to quickly generate a well-structured description and automatically fill the properties attributes or click "Edit" to add one manually.',
      recommended: 'Recommended: {{min}}-{{max}} characters',
      title: 'Property Description',
    },
    propertyDetails: {
      attributes: {
        empty: 'No property attributes have been added yet.',
        title: 'Attributes',
      },
      buttons: {
        edit: 'Edit',
        editWithAI: 'Edit with AI',
        generateWithAI: 'Generate with AI',
        generating: 'Generating...',
      },
      description: {
        empty: 'No description has been added yet.',
        title: 'Property Description',
      },
      situationLocation: {
        empty: 'No situation and location description has been added yet.',
        title: 'Situation & Location',
      },
      subtitle: 'Detailed description and information about the property.',
      title: 'Property Details',
    },
    reportModal: {
      cancel: 'Cancel',
      confirmDelete: 'Are you sure you want to delete this report?',
      createNew: 'Create new report',
      delete: 'Delete',
      edit: 'Edit',
      noReports: 'No reports created yet',
      sharedWithOffice: 'Shared with office',
      title: 'PDF Reports',
      typeBuildingAnalysis: 'Building Analysis',
      typeEstimate: 'Estimate',
      typeLandAnalysis: 'Land Analysis',
      typeLandGroupingAnalysis: 'Land grouping analysis',
      untitledReport: 'Untitled Report',
      updated: 'Updated',
    },
    savedItems: {
      buildingPermits: {
        empty: 'No saved building permits',
        remove: 'Remove from saved',
        title: 'Saved Building Permits',
      },
      listings: {
        empty: 'No saved listings',
        remove: 'Remove from saved',
        title: 'Saved Listings',
      },
      transactions: {
        empty: 'No saved transactions',
        remove: 'Remove from saved',
        title: 'Saved Transactions',
      },
    },
    tags: {
      addExistingTag: 'Add Existing Tag',
      addSelectedTag: 'Add Selected Tag',
      addTags: 'Add tags',
      cancel: 'Cancel',
      createAndAdd: 'Create & Add',
      createNewTag: 'Create New Tag',
      currentTags: 'Current Tags',
      manageTitle: 'Manage Tags',
      noAvailableTags: 'No available tags',
      noMatchingTags: 'No matching tags found',
      noTagsYet: 'No tags assigned yet',
      primary: 'Primary',
      primaryConfirm: 'The existing primary tag will be changed to secondary. Continue?',
      primaryWarning: 'The existing primary tag will become secondary',
      searchPlaceholder: 'Search tags...',
      secondary: 'Secondary',
      setPrimary: 'Set as Primary Tag',
      tagNamePlaceholder: 'Tag name...',
    },
    tooltipMetric: {
      categories: 'Categories:',
      howCalculated: "How it's calculated:",
      whatItMeans: 'What it means:',
      whyItMatters: 'Why it matters:',
    },
  },
}
